/**
 * Why an order was canceled, in words a customer can act on without calling. Every cancellation
 * message (text, email, tracking page) says what happened, what happened to their money, and what
 * to do next, from the reason recorded on the order's timeline.
 */

/** Reasons staff pick when they cancel. Stored on the timeline as "[code] note to the customer". */
export const STAFF_CANCEL_REASONS = [
  { code: 'sold_out', label: 'Ran out of an item' },
  { code: 'equipment', label: 'Oven or equipment problem' },
  { code: 'closed', label: 'Closed early (weather, event change)' },
  { code: 'customer_request', label: 'Customer asked us to cancel' },
  { code: 'duplicate', label: 'Duplicate order' },
  { code: 'payment_issue', label: "Payment couldn't be taken" },
  { code: 'other', label: 'Something else' },
] as const;
export type StaffCancelCode = (typeof STAFF_CANCEL_REASONS)[number]['code'];

/** The reason text to record for a staff cancellation. */
export function staffCancelReason(code: StaffCancelCode, noteToCustomer: string): string {
  const note = noteToCustomer.trim().replace(/\s+/g, ' ');
  return `[${code}]${note ? ` ${note}` : ''}`;
}

export interface CancellationInput {
  /** The reason on the CANCELED timeline entry. */
  reason: string | null;
  /** Who canceled: CUSTOMER, STAFF, SYSTEM, WEBHOOK. */
  actor: string | null;
  /** A payment was authorized (a hold on the card) before the cancellation. */
  authorized: boolean;
  /** Crave Credit that was put back when the order was canceled. */
  creditReturnedCents: number;
}

export interface Cancellation {
  kind:
    | 'payment_timeout'
    | 'payment_system'
    | 'customer'
    | 'pickup_missed'
    | StaffCancelCode
    | 'unknown';
  /** One or two sentences: what happened. */
  why: string;
  /** Short form for a text message. */
  whyShort: string;
  /** What happened to their money. */
  money: string;
  moneyShort: string;
  /** What to do now. */
  next: string;
  /** Their cart and a new pickup time fix it: offer to order again. */
  orderAgain: boolean;
  /** A note staff wrote for the customer, shown as written. */
  staffNote: string | null;
}

const WHY: Record<
  Cancellation['kind'],
  { why: string; short: string; next: string; again: boolean }
> = {
  payment_timeout: {
    why: "The payment wasn't finished before the time we were holding your pickup ran out, so we released that time for other customers.",
    short: "the payment wasn't finished in time",
    next: 'Still hungry? Place the order again, pick a new pickup time, and pay with a card or another way.',
    again: true,
  },
  payment_system: {
    why: 'Our payment system had a problem while you were checking out, so the order could not go through.',
    short: 'our payment system had a problem',
    next: 'Please try again in a few minutes. Sorry about that.',
    again: true,
  },
  customer: {
    why: 'You canceled this order.',
    short: 'you canceled it',
    next: 'Changed your mind? You can order again anytime.',
    again: true,
  },
  pickup_missed: {
    why: "We weren't able to start your order before your pickup time passed. That's on us, and we're sorry.",
    short: "we couldn't make it before your pickup time",
    next: "We would love to make it right. Text us and we'll help.",
    again: true,
  },
  sold_out: {
    why: 'We ran out of something in your order before we could make it.',
    short: 'we ran out of an item in it',
    next: "Sorry about that. Check the menu for what's available now.",
    again: true,
  },
  equipment: {
    why: "We had an equipment problem (like our oven) and couldn't make your order.",
    short: 'we had an equipment problem',
    next: "We're sorry for the trouble. Please try again another time.",
    again: false,
  },
  closed: {
    why: 'We had to close early (weather or an event change), so we could not make your order.',
    short: 'we had to close early',
    next: 'Sorry about that. Follow cravepizzafl.com for our next service day.',
    again: false,
  },
  customer_request: {
    why: 'You asked us to cancel this order, so we did.',
    short: 'you asked us to',
    next: 'You can order again anytime.',
    again: true,
  },
  duplicate: {
    why: 'This was a duplicate of another order you placed, so we canceled the extra one.',
    short: 'it was a duplicate of another order',
    next: 'Your other order is still on. Nothing else to do.',
    again: false,
  },
  payment_issue: {
    why: "We couldn't take the payment for this order.",
    short: "we couldn't take the payment",
    next: 'Place the order again and try a different way to pay.',
    again: true,
  },
  other: {
    why: 'We had to cancel this order.',
    short: 'we had to cancel it',
    next: 'Sorry for the trouble. Text us if you have any questions.',
    again: false,
  },
  unknown: {
    why: 'This order was canceled.',
    short: 'it was canceled',
    next: 'Text us if you have any questions.',
    again: true,
  },
};

function kindOf(
  reason: string | null,
  actor: string | null,
): {
  kind: Cancellation['kind'];
  note: string | null;
} {
  const r = (reason ?? '').trim();
  const coded = /^\[([a-z_]+)\]\s*(.*)$/s.exec(r);
  if (coded && STAFF_CANCEL_REASONS.some((x) => x.code === coded[1]))
    return { kind: coded[1] as StaffCancelCode, note: coded[2]?.trim() || null };
  const lower = r.toLowerCase();
  if (lower.includes('payment was not completed') || lower.includes('payment never started'))
    return { kind: 'payment_timeout', note: null };
  if (lower.includes('payment provider unavailable')) return { kind: 'payment_system', note: null };
  if (lower.includes('pickup time passed')) return { kind: 'pickup_missed', note: null };
  if (actor === 'CUSTOMER') return { kind: 'customer', note: null };
  // Older staff cancellations wrote free text meant for the team, not the customer: never shown.
  return { kind: 'unknown', note: null };
}

const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export function explainCancellation(input: CancellationInput): Cancellation {
  const { kind, note } = kindOf(input.reason, input.actor);
  const w = WHY[kind];
  const credit =
    input.creditReturnedCents > 0
      ? ` The ${dollars(input.creditReturnedCents)} of Crave Credit you used is back in your account.`
      : '';
  const money = input.authorized
    ? `You were not charged. The hold on your payment is released; your bank may still show it as pending for a few business days before it disappears.${credit}`
    : `You were not charged.${credit}`;
  return {
    kind,
    why: w.why,
    whyShort: w.short,
    money,
    moneyShort: input.authorized
      ? 'You were not charged and the payment hold is released.'
      : 'You were not charged.',
    next: w.next,
    orderAgain: w.again,
    staffNote: note,
  };
}
